import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 62. FEATURE FLAGS
// Permet d'ACTIVER PROGRESSIVEMENT certaines fonctionnalités :
//   new_ai_booking_engine · advanced_inventory · new_dashboard · mobile_payments
// Un flag peut être défini au niveau plateforme (organization_id NULL) puis
// surchargé par entreprise. Le déploiement progressif se fait via rollout_percentage.
// ─────────────────────────────────────────────────────────────

export const FLAG_TEMPLATES: { key: string; label: string; desc: string; icon: string }[] = [
  { key: 'new_ai_booking_engine', label: 'Nouveau moteur de réservation IA', desc: 'Moteur de réservation assisté par IA.', icon: 'ri-robot-2-line' },
  { key: 'advanced_inventory', label: 'Inventaire avancé', desc: 'Stock multi-entrepôts et prévisions.', icon: 'ri-stack-line' },
  { key: 'new_dashboard', label: 'Nouveau tableau de bord', desc: 'Refonte du dashboard central.', icon: 'ri-dashboard-line' },
  { key: 'mobile_payments', label: 'Paiements mobiles', desc: 'Encaisse mobile et QR.', icon: 'ri-smartphone-line' },
];

export function flagMeta(key: string | null | undefined) {
  return FLAG_TEMPLATES.find((f) => f.key === key) || { key: key || 'flag', label: key || 'Flag', desc: 'Fonctionnalité contrôlée.', icon: 'ri-toggle-line' };
}

export interface CoreFeatureFlag {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  flag_key: string;
  label: string | null;
  description: string | null;
  enabled: boolean | null;
  rollout_percentage: number | null;
  scope: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Liste les flags (fusionnés plateforme → entreprise) pour une organisation. */
export async function listFeatureFlags(organizationId: number): Promise<CoreFeatureFlag[]> {
  const { data, error } = await supabase
    .from('core_feature_flags')
    .select('*')
    .or(`organization_id.is.null,organization_id.eq.${organizationId}`)
    .order('flag_key', { ascending: true });
  if (error) throw error;
  const byKey = new Map<string, CoreFeatureFlag>();
  (data || []).forEach((row) => {
    const f = row as CoreFeatureFlag;
    const existing = byKey.get(f.flag_key);
    if (!existing || f.organization_id != null) byKey.set(f.flag_key, f);
  });
  // Complète avec les modèles non présents.
  FLAG_TEMPLATES.forEach((t) => {
    if (!byKey.has(t.key)) {
      byKey.set(t.key, {
        id: -1, tenant_id: null, organization_id: null, flag_key: t.key, label: t.label,
        description: t.desc, enabled: false, rollout_percentage: 0, scope: 'platform',
        created_at: null, updated_at: null,
      });
    }
  });
  return Array.from(byKey.values());
}

/**
 * Évalue si un flag est actif pour une organisation.
 * Le rollout_percentage est mappé de façon déterministe sur l'id d'organisation,
 * ce qui garantit un déploiement stable et progressif.
 */
export async function isFlagEnabled(organizationId: number, flagKey: string): Promise<boolean> {
  const { data } = await supabase
    .from('core_feature_flags')
    .select('enabled, rollout_percentage, organization_id')
    .eq('flag_key', flagKey)
    .or(`organization_id.is.null,organization_id.eq.${organizationId}`);
  const rows = (data || []) as { enabled: boolean | null; rollout_percentage: number | null; organization_id: number | null }[];
  const orgRow = rows.find((r) => r.organization_id != null);
  const row = orgRow || rows[0];
  if (!row) return false;
  if (row.enabled === false) return false;
  const pct = row.rollout_percentage ?? 0;
  if (pct >= 100 || pct <= 0) return true;
  // Déterministe : organisation_id modulo 100 < pourcentage.
  return (organizationId % 100) < pct;
}

export interface SetFlagInput {
  tenantId?: number | null;
  organizationId: number;
  flagKey: string;
  enabled?: boolean;
  rolloutPercentage?: number;
  label?: string | null;
  description?: string | null;
}

/** Active/désactive un flag pour une entreprise (crée la surcharge si besoin). */
export async function setFeatureFlag(input: SetFlagInput): Promise<void> {
  const t = flagMeta(input.flagKey);
  const existing = await supabase
    .from('core_feature_flags')
    .select('id, enabled, rollout_percentage')
    .eq('organization_id', input.organizationId)
    .eq('flag_key', input.flagKey)
    .maybeSingle();

  const payload = {
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    flag_key: input.flagKey,
    label: input.label ?? t.label,
    description: input.description ?? t.desc,
    enabled: input.enabled ?? (existing.data?.enabled as boolean) ?? false,
    rollout_percentage: input.rolloutPercentage ?? (existing.data?.rollout_percentage as number) ?? 0,
    scope: 'organization',
    updated_at: new Date().toISOString(),
  };
  if (existing.data?.id) {
    const { error } = await supabase.from('core_feature_flags').update(payload).eq('id', existing.data.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('core_feature_flags').insert(payload);
    if (error) throw error;
  }
}

export async function createFeatureFlag(input: {
  organizationId: number;
  flagKey: string;
  label: string;
  description?: string | null;
  rolloutPercentage?: number;
  enabled?: boolean;
}): Promise<void> {
  const existing = await supabase
    .from('core_feature_flags')
    .select('id')
    .eq('organization_id', input.organizationId)
    .eq('flag_key', input.flagKey)
    .maybeSingle();
  const payload = {
    organization_id: input.organizationId,
    flag_key: input.flagKey,
    label: input.label,
    description: input.description ?? null,
    enabled: input.enabled ?? false,
    rollout_percentage: input.rolloutPercentage ?? 0,
    scope: 'organization',
    updated_at: new Date().toISOString(),
  };
  if (existing.data?.id) {
    await supabase.from('core_feature_flags').update(payload).eq('id', existing.data.id);
  } else {
    const { error } = await supabase.from('core_feature_flags').insert(payload);
    if (error) throw error;
  }
}

export async function deleteFeatureFlag(id: number): Promise<void> {
  const { error } = await supabase.from('core_feature_flags').delete().eq('id', id);
  if (error) throw error;
}

export interface FlagStats {
  total: number;
  enabled: number;
  progressive: number;
}

export async function fetchFlagStats(organizationId: number): Promise<FlagStats> {
  const flags = await listFeatureFlags(organizationId);
  let enabled = 0;
  let progressive = 0;
  flags.forEach((f) => {
    if (f.enabled) enabled += 1;
    if ((f.rollout_percentage ?? 0) > 0 && (f.rollout_percentage ?? 0) < 100) progressive += 1;
  });
  return { total: flags.length, enabled, progressive };
}